<template>
    <div class="fixed z-10 inset-0 overflow-y-auto backdrop-blur-[2px]" role="dialog" @click.stop>
        <div
            :class="{ dark: store.settings.darkMode }"
            class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 opacity-25 bg-gray-500 transition-opacity"
                 @click="waapiStore.showCampaignModal = false; waapiStore.resetForm()"></div>
            <div
                class="inline-block z-20 bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-3xl">
                <div class="p-4 min-w-0 md:min-w-120 max-w-160">
                    <div class="sm:flex sm:items-start px-3">
                        <div class="mt-3 text-center sm:mt-0 w-full sm:text-left">
                            <div class="flex flex-row mb-6">
                                <img v-if="!store.settings.darkMode" alt="official_icon" class="mr-4" height="20"
                                     src="@src/assets/vectors/official_icon.svg"
                                     width="20">
                                <img v-else alt="official_icon" class="mr-4" height="20"
                                     src="@src/assets/vectors/official_icon_white.svg"
                                     width="20">
                                <h3 class="text-lg leading-6 text-gray-900 dark:text-gray-100">
                                    Nova Campanha API Oficial
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div class="p-4 w-full">
                        <CreateStep v-if="waapiStore.step === 'info'" :costumerToken="props.costumerToken ?? ''"
                                    :form="props.form"/>
                        <HeaderStep v-if="waapiStore.step === 'header'"/>
                        <ButtonStep v-if="waapiStore.step === 'buttons'"/>
                        <ReviewStep v-if="waapiStore.step === 'review'"/>
                    </div>
                    <div class="flex justify-between mt-4">
                        <Button
                            v-if="waapiStore.step === 'info'"
                            class="p-3 px-6 mb-4 mt-4 font-bold text-white bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700"
                            @click="waapiStore.showCampaignModal = false; waapiStore.resetForm()"
                        >
                            Cancelar
                        </Button>
                        <Button
                            v-else
                            class="p-3 px-6 mb-4 mt-4 font-bold text-white bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700"
                            @click="waapiStore.previousStep()"
                        >
                            Voltar
                        </Button>

                        <Button
                            v-if="waapiStore.step !== 'review'"
                            :class="{ 'opacity-50 cursor-not-allowed': !isValidForm }"
                            :disabled="!isValidForm"
                            :loading="waapiStore.loading"
                            class="p-3 px-6 ml-4 mb-4 mt-4 font-bold text-white bg-indigo-400 hover:bg-indigo-500 dark:bg-indigo-600 dark:hover:bg-indigo-700"
                            @click="waapiStore.nextStep()"
                        >
                            Continuar
                        </Button>
                        <Button
                            v-else-if="waapiStore.campaignForm.scheduled === 'immediate'"
                            :loading="waapiStore.loading"
                            class="p-3 px-6 ml-4 mb-4 mt-4 font-bold text-white bg-green-400 hover:bg-green-500 dark:bg-green-600 dark:hover:bg-green-700"
                            @click="submit"
                        >
                            Enviar
                        </Button>
                        <Button
                            v-else
                            :loading="waapiStore.loading"
                            class="p-3 px-6 ml-4 mb-4 mt-4 font-bold text-white bg-green-400 hover:bg-green-500 dark:bg-green-600 dark:hover:bg-green-700"
                            @click="submit"
                        >
                            Agendar
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {computed, defineAsyncComponent, ref} from "vue";
import Button from "@src/components/ui/inputs/Button.vue";
import {useWAAPIStore} from "@src/stores/waapi";
import useStore from "@src/stores/store";
import {useChatSessionStore} from "@src/stores/chatSession";
import WaapiService from "@src/services/waapi"
import {Toast} from "@src/plugins/toast";


const CreateStep = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/CreateStep.vue"));
const HeaderStep = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/HeaderStep.vue"));
const ButtonStep = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/ButtonStep.vue"));
const ReviewStep = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/ReviewStep.vue"));

const store = useStore()
const sessionStore = useChatSessionStore()
const waapiStore = useWAAPIStore()

const props = defineProps<{
    costumerToken?: string,
    sessionId?: number | string,
    form?: any
}>()

const emits = defineEmits(['reload'])
const tokens = ref<string[]>([])

const hasHeader = computed(() => {
    return waapiStore.campaignForm.components.some(component => component.type === 'HEADER')
})
const hasButtons = computed(() => {
    return waapiStore.campaignForm.components.some(component => component.type === 'BUTTONS')
})

const isValidForm = computed(() => {
    if (waapiStore.step === 'info') {
        return waapiStore.campaignForm.model_id !== 0
    } else if (waapiStore.step === 'header') {
        return waapiStore.campaignForm.components.length > 0 && hasHeader.value
    } else if (waapiStore.step === 'buttons') {
        return waapiStore.campaignForm.components.length > 0 && hasButtons.value
    }
})

const submit = async () => {
    waapiStore.loading = true;
    const campaign = waapiStore.campaignForm
    let isScheduled = waapiStore.campaignForm.scheduled === 'scheduled'

    if (waapiStore.bulk) {
        waapiStore.sessions.forEach(session => {
            tokens.value.push(session.costumer?.token)
        })
        await WaapiService.uploadContacts(campaign.id, tokens.value)
    } else {
        await WaapiService.uploadContact(campaign.id, props.costumerToken ?? sessionStore.selectedSession?.costumer?.token ?? '')
    }

    const formData = new FormData()

    const sessionId = props.sessionId
        ? props.sessionId.toString()
        : (!props.costumerToken ? sessionStore.selectedSession?.id?.toString() ?? '' : '');

    formData.append('session', sessionId);
    formData.append('id', campaign.id.toString());
    await WaapiService.sendCampaign(formData)
    if (isScheduled) {
        emits('reload')
    }
    waapiStore.loading = false;
    sessionStore.actionSessions = []
    sessionStore.openActionsBulk = false
    waapiStore.showCampaignModal = false;
    tokens.value = []
    await Toast.fire({
        title: isScheduled ? 'Campanha agendada com sucesso!' : 'Campanha enviada com sucesso!',
        icon: 'success'
    });
    waapiStore.cleanCampaign()
}
</script>

<style scoped>

</style>